import { useEffect, useState } from 'react';
import {
  RuleError,
  applyDeal,
  continueMatch as continueMatchState,
  currentDealer,
  dealerForDeal,
  dealsDealtBySeat,
  handsPerPlayer,
  matchStatus,
  playerStats,
  rangPlayerOfDeal,
  schedule,
  teamOfSeat,
  totals,
  trumpCallerSeat,
  turnInfo,
  type DealInput,
  type HouseRules,
} from '../game/match';
import { cleanName } from '../shared/constants';
import type { Teams } from '../shared/schemas';
import { GuaaDetailsSheet } from '../components/GuaaDetails';
import { showGuaaPopup } from '../components/GuaaPopup';
import { VoicePicker } from '../components/VoicePicker';
import { HouseRulesPanel } from '../components/HouseRules';
import { DealerStrip, HistoryTable } from '../components/Schedule';
import { Scoreboard } from '../components/Scoreboard';
import { TableView } from '../components/TableView';
import { confirmDialog } from '../components/Confirm';
import { FinishPreview } from '../components/FinishPreview';
import { HandPips, turnHands } from '../components/NowDealing';
import { RoundComplete } from '../components/RoundComplete';
import { Name, Sheet, Spinner, SuitGlyph, Toggle } from '../components/ui';
import { Link, navigate } from '../lib/router';
import { continueMatch, finishMatch, getToken, patchMatch, recordDeal, toMatchState, undoDeal } from '../lib/store';
import { toast } from '../lib/toast';
import { SEVEN_GUAA_MILESTONE, afterDealSpeech, announce, dealingPhrases, setVoiceEnabled, useVoiceEnabled, voiceSupported } from '../lib/voice';
import { RecordDealSheet } from './RecordDealSheet';
import { BackButton, NotFound, SyncBadge, useMatchData } from './shared';

export async function shareOrCopy(text: string, url: string) {
  try {
    if (navigator.share) {
      await navigator.share({ text, url });
      return;
    }
  } catch (err) {
    if ((err as Error).name === 'AbortError') return;
  }
  try {
    await navigator.clipboard.writeText(`${text} ${url}`.trim());
    toast('Copied to clipboard', 'success');
  } catch {
    toast('Could not copy. Long-press the code to copy it.', 'error');
  }
}

export function Dashboard({ code }: { code: string }) {
  const { data, error, canEdit } = useMatchData(code);
  const [recordOpen, setRecordOpen] = useState(false);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [rulesDraft, setRulesDraft] = useState<HouseRules | null>(null);
  const [rulesUnlocked, setRulesUnlocked] = useState(false);
  const [teamsDraft, setTeamsDraft] = useState<Teams | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [voicePickerOpen, setVoicePickerOpen] = useState(false);
  const voiceOn = useVoiceEnabled();

  const needsSpin = data && data.match.firstDealerSeat === null;
  useEffect(() => {
    if (needsSpin) navigate(`/m/${code}/wheel`, { replace: true });
  }, [needsSpin, code]);

  if (error && !data) return <NotFound message={error} />;
  if (!data || needsSpin) return <Spinner label="Loading match" />;

  const { match, deals } = data;
  const state = toMatchState(data);
  const status = matchStatus(state);
  const t = totals(deals);
  const dealer = currentDealer(state);
  const caller = dealer === null ? null : trumpCallerSeat(dealer, match.rules);
  const finished = status.phase === 'finished';
  const lastTrump = deals.at(-1)?.trumpSuit;
  const turn = turnInfo(state, status.nextDealNo);
  const stats = playerStats(state);
  const guaaBySeat = { 0: stats[0].guaa, 1: stats[1].guaa, 2: stats[2].guaa, 3: stats[3].guaa };

  const save = (input: DealInput) => {
    try {
      recordDeal(code, input);
      setRecordOpen(false);
      const next = applyDeal(state, input);
      const savedDeal = next.deals.at(-1)!;
      const phase = matchStatus(next).phase;
      if (phase === 'finished') navigate(`/m/${code}/end`);
      // Round over: bring the "keep playing or finish" card into view.
      else if (phase === 'roundComplete') window.scrollTo({ top: 0, behavior: 'smooth' });
      // Spoken from the tap, so mobile browsers allow it: the Guaa cheer first, then the next hand.
      announce(afterDealSpeech(next, match.seats, match.teams));
      if (savedDeal.result === 'guaa') {
        const seat = rangPlayerOfDeal(savedDeal, match.rules);
        const team = savedDeal.trumpTeam ? match.teams[savedDeal.trumpTeam] : null;
        if (seat !== null && team) {
          const nextTotals = totals(next.deals);
          const teamTotal = savedDeal.trumpTeam === 'A' ? nextTotals.guaaA : nextTotals.guaaB;
          showGuaaPopup({
            name: match.seats[seat]!.name,
            avatarKey: match.seats[seat]!.avatarKey,
            team,
            guaa: savedDeal.guaaA + savedDeal.guaaB,
            isCourt: savedDeal.isCourt,
            milestone: teamTotal === SEVEN_GUAA_MILESTONE ? teamTotal : null,
          });
        }
      }
    } catch (err) {
      toast(err instanceof RuleError ? err.message : 'Could not record the deal.', 'error');
    }
  };

  const undo = async () => {
    const last = deals.at(-1);
    if (!last) return;
    const rang = last.trumpTeam ? match.teams[last.trumpTeam] : null;
    const ok = await confirmDialog({
      icon: '↶',
      title: `Undo deal ${last.dealNo}?`,
      body: (
        <>
          <span className="mb-2 inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 font-semibold text-ink ring-1 ring-ink/10">
            {rang && <span className="h-2.5 w-2.5 rounded-full" style={{ background: rang.color }} aria-hidden />}
            {rang?.name ?? 'Rang team'} ·{' '}
            {last.result === 'saved' ? <span className="text-success-700">✓ Saved</span> : <span className="text-suit-red">+{last.guaaA + last.guaaB} Guaa</span>}
          </span>
          <p>
            Dealt by <Name className="font-semibold text-ink">{match.seats[last.dealerSeat]!.name}</Name>. It goes back to being the current hand.
          </p>
        </>
      ),
      confirmLabel: 'Undo deal',
      cancelLabel: 'Keep it',
      tone: 'danger',
    });
    if (!ok) return;
    undoDeal(code);
    toast(`Deal ${last.dealNo} removed`, 'info');
  };

  const openRules = () => {
    setRulesDraft(match.rules);
    setRulesUnlocked(false);
    setOptionsOpen(false);
    setRulesOpen(true);
  };

  const saveRules = () => {
    if (!rulesDraft) return;
    try {
      patchMatch(code, { rules: rulesDraft });
      setRulesOpen(false);
      toast(deals.length ? 'Rules changed, deals re-scored' : 'Rules saved', 'success');
    } catch (err) {
      toast(err instanceof RuleError ? err.message : 'Could not change the rules.', 'error', 6000);
    }
  };

  const viewUrl = `${location.origin}/m/${code}`;
  const roundDone = status.phase === 'roundComplete';
  const progress = finished
    ? `Finished after ${deals.length} deals`
    : roundDone
      ? `Round complete · ${deals.length} deals`
      : `Deal ${status.nextDealNo} of ${status.scheduledDeals}`;

  const keepPlaying = (hands: number) => {
    try {
      continueMatch(code, hands);
      toast(`${hands * 4} more deals added`, 'success');
      announce(dealingPhrases(continueMatchState(state, hands), match.seats));
    } catch (err) {
      toast(err instanceof RuleError ? err.message : 'Could not extend the match.', 'error');
    }
  };

  const finish = async () => {
    setOptionsOpen(false);
    const ok = await confirmDialog({
      icon: '🏁',
      title: 'Finish the match?',
      body: <FinishPreview teams={match.teams} seats={match.seats} t={t} leader={status.leader} deals={deals.length} />,
      confirmLabel: '🏁 Finish',
      cancelLabel: 'Keep playing',
    });
    if (!ok) return;
    try {
      finishMatch(code);
      setOptionsOpen(false);
      navigate(`/m/${code}/end`);
    } catch (err) {
      toast(err instanceof RuleError ? err.message : 'Could not finish the match.', 'error');
    }
  };
  const pct = Math.min(100, (deals.length / status.scheduledDeals) * 100);

  return (
    <main className="mx-auto max-w-lg space-y-4 px-4 pb-40">
      <header className="flex items-center gap-2 pt-[max(1rem,env(safe-area-inset-top))]">
        <BackButton href="/home" label="Home" />
        <button
          type="button"
          className="min-w-0 flex-1 text-left"
          onClick={() => shareOrCopy(`Follow our Rang match: ${code}`, viewUrl)}
          aria-label={`Match code ${code}. Share`}
        >
          <span className="eyebrow block text-[10px] text-cream/60">Match code</span>
          <span className="font-mono text-lg font-bold tracking-[0.2em]">{code}</span>
        </button>
        <SyncBadge code={code} />
        <button type="button" className="btn btn-ghost h-11 w-11 rounded-full p-0" onClick={() => setOptionsOpen(true)} aria-label="Match options">
          ⋯
        </button>
      </header>

      {/* The whole card opens the details; the "Who got Guaa?" button is the keyboard/screen-reader target. */}
      <section
        className="card cursor-pointer px-4 py-3 transition hover:shadow-[0_10px_30px_-12px_rgb(0_0_0/0.6),0_0_0_2px_var(--color-gold)] active:scale-[0.99]"
        aria-label="Scoreboard"
        onClick={() => setDetailsOpen(true)}
      >
        <Scoreboard teams={match.teams} t={t} />
        <div className="mt-2.5 h-1.5 overflow-hidden rounded-full bg-ink/10" role="progressbar" aria-valuemin={0} aria-valuemax={status.scheduledDeals} aria-valuenow={deals.length} aria-label="Deals played">
          <div
            className="animate-shimmer h-full rounded-full transition-[width] duration-500"
            style={{
              width: `${pct}%`,
              backgroundImage: 'linear-gradient(90deg, var(--color-success-600), var(--color-accent-violet), var(--color-success-500), var(--color-success-600))',
            }}
          />
        </div>
        <div className="mt-1.5 flex items-center justify-between text-xs font-semibold text-ink-soft">
          <span>{progress}</span>
          <button
            type="button"
            className="-mr-1 rounded-full px-2 py-1 font-bold text-ink hover:bg-ink/5"
            onClick={(e) => {
              e.stopPropagation();
              setDetailsOpen(true);
            }}
            aria-haspopup="dialog"
          >
            Who got Guaa? ›
          </button>
        </div>
      </section>

      {finished ? (
        <Link href={`/m/${code}/end`} className="btn btn-primary w-full">
          🏆 Match over · see the result
        </Link>
      ) : roundDone ? (
        <RoundComplete
          teams={match.teams}
          t={t}
          leader={status.leader}
          deals={deals.length}
          canEdit={canEdit}
          onKeepPlaying={keepPlaying}
          onFinish={finish}
        />
      ) : null}

      <section className="panel p-4" aria-label="Table">
        <TableView
          seats={match.seats}
          teams={match.teams}
          dealer={dealer}
          caller={caller}
          dealt={dealsDealtBySeat(state)}
          dealsPerPlayer={handsPerPlayer(match.rules)}
          guaa={guaaBySeat}
          callerTeamName={caller !== null && caller !== dealer ? match.teams[teamOfSeat(caller)].name : null}
          hand={
            !finished && !roundDone && dealer !== null
              ? (() => {
                  const { dealInTurn, dealsPerTurn } = turnInfo(state, status.nextDealNo);
                  const lastOfTurn = dealInTurn === dealsPerTurn;
                  const next = dealerForDeal(state, status.nextDealNo - dealInTurn + dealsPerTurn + 1);
                  const showNext = lastOfTurn && next !== null && next !== dealer && next !== caller;
                  return {
                    dealInTurn,
                    dealsPerTurn,
                    lastOfTurn,
                    nextDealerName: showNext ? match.seats[next]!.name : null,
                    pips: <HandPips deals={turnHands(state, status.nextDealNo)} current />,
                  };
                })()
              : undefined
          }
          onSpeak={voiceSupported() ? () => announce(dealingPhrases(state, match.seats, { withTurnEnd: false }), { force: true }) : undefined}
          center={
            <div className="space-y-1">
              <p className="eyebrow text-[10px] text-cream/60">{finished ? 'Final' : roundDone ? 'Round done' : 'Deal'}</p>
              <p className="font-display text-3xl leading-none font-extrabold text-cream">{finished || roundDone ? deals.length : status.nextDealNo}</p>
              {lastTrump ? (
                <p className="text-[11px] text-cream/70">
                  Last Rang <SuitGlyph suit={lastTrump} onDark className="text-sm" />
                </p>
              ) : (
                match.rules.trumpCaller === 'highestBidder' && <p className="text-[11px] text-cream/70">Rang by bid</p>
              )}
            </div>
          }
        />
      </section>

      <section aria-labelledby="sched-h">
        <div className="mb-2 flex items-baseline justify-between">
          <h2 id="sched-h" className="eyebrow text-cream/70">
            Dealer schedule
          </h2>
          <span className="text-xs text-cream/60">{match.rules.turnDirection === 'anticlockwise' ? '↺ anticlockwise' : '↻ clockwise'}</span>
        </div>
        <DealerStrip slots={schedule(state)} seats={match.seats} teams={match.teams} rules={match.rules} />
      </section>

      <section className="card p-4" aria-labelledby="hist-h">
        <div className="mb-2 flex items-center justify-between">
          <h2 id="hist-h" className="font-display text-xl font-bold">
            Deal history
          </h2>
          {canEdit && deals.length > 0 && (
            <button type="button" className="btn btn-outline-ink min-h-10 px-3 text-sm" onClick={undo}>
              ↶ Undo last
            </button>
          )}
        </div>
        <HistoryTable deals={deals} seats={match.seats} teams={match.teams} />
      </section>

      {canEdit && !finished && dealer !== null && (
        <div className="fixed inset-x-0 bottom-0 z-10 bg-gradient-to-t from-felt-950 via-felt-950/92 to-transparent pt-6 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          <div className="mx-auto max-w-lg px-4">
            <button
              type="button"
              className="btn group relative h-16 w-full flex-col gap-0.5 overflow-hidden rounded-2xl border-2 border-gold-soft/60 text-cream shadow-[inset_0_1.5px_0_0_rgb(255_255_255/0.4),0_5px_0_0_#4a2f6e,0_16px_32px_-10px_rgb(124_91_208/0.65)] transition active:translate-y-[3px] active:shadow-[inset_0_1.5px_0_0_rgb(255_255_255/0.25),0_2px_0_0_#4a2f6e,0_8px_16px_-10px_rgb(124_91_208/0.5)]"
              style={{ background: 'linear-gradient(110deg, #8768e0 0%, #ad5aa8 45%, #cc5c82 70%, #e0b254 100%)' }}
              onClick={() => setRecordOpen(true)}
            >
              <span className="pointer-events-none absolute inset-0 animate-sheen bg-[linear-gradient(115deg,transparent_20%,rgb(255_255_255/0.45)_42%,transparent_60%)] bg-[length:220%_100%]" />
              <span className="relative text-lg leading-tight font-extrabold">
                {turn.dealsPerTurn > 1 ? `Record hand ${turn.dealInTurn} of ${turn.dealsPerTurn}` : `Record deal ${status.nextDealNo}`}
              </span>
              <span className="relative text-xs font-semibold text-cream/80">
                Deal {status.nextDealNo} · <Name>{match.seats[dealer]!.name}</Name> dealing
              </span>
              <span
                className="absolute top-1/2 right-4 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-full bg-white/20 text-lg transition-transform group-active:translate-x-0.5"
                aria-hidden
              >
                →
              </span>
            </button>
          </div>
        </div>
      )}

      {dealer !== null && (
        <RecordDealSheet
          key={status.nextDealNo}
          open={recordOpen}
          onClose={() => setRecordOpen(false)}
          onSave={save}
          dealNo={status.nextDealNo}
          dealer={match.seats[dealer]!}
          caller={caller === null ? null : match.seats[caller]!}
          callerTeam={caller === null ? null : teamOfSeat(caller)}
          rules={match.rules}
          teams={match.teams}
        />
      )}

      <GuaaDetailsSheet open={detailsOpen} onClose={() => setDetailsOpen(false)} state={state} seats={match.seats} teams={match.teams} />

      <VoicePicker open={voicePickerOpen} onClose={() => setVoicePickerOpen(false)} />

      <Sheet open={optionsOpen} onClose={() => setOptionsOpen(false)} title="Match options">
        <div className="grid gap-2">
          <button type="button" className="btn btn-outline-ink justify-start" onClick={() => shareOrCopy(`Follow our Rang match: ${code}`, viewUrl)}>
            🔗 Share view-only link
          </button>
          {canEdit && (
            <button
              type="button"
              className="btn btn-outline-ink justify-start"
              onClick={() => shareOrCopy('Help keep score for our Rang match:', `${viewUrl}#edit=${getToken(code)}`)}
            >
              ✍️ Share editor link (lets others record deals)
            </button>
          )}
          {voiceSupported() && (
            <div className="rounded-xl px-3 py-1 ring-1 ring-ink/25">
              <Toggle
                label="🔊 Voice announcements"
                hint="Says in Hindi who is dealing which hand, e.g. “Ashu pehla haath baant raha hai”."
                checked={voiceOn}
                onChange={(on) => {
                  setVoiceEnabled(on);
                  if (on) announce(dealingPhrases(state, match.seats, { withTurnEnd: false }));
                }}
              />
              {voiceOn && (
                <button
                  type="button"
                  className="mt-1 -ml-1 rounded px-1 text-[11px] font-semibold text-ink-soft underline decoration-dotted"
                  onClick={() => {
                    setOptionsOpen(false);
                    setVoicePickerOpen(true);
                  }}
                >
                  Mispronouncing something? Choose a different voice ›
                </button>
              )}
            </div>
          )}
          {canEdit && !finished && deals.length > 0 && (
            <button type="button" className="btn btn-outline-ink justify-start" onClick={finish}>
              🏁 Finish match now
            </button>
          )}
          <button type="button" className="btn btn-outline-ink justify-start" onClick={openRules}>
            📜 House rules {deals.length > 0 && '(locked)'}
          </button>
          {canEdit && (
            <button
              type="button"
              className="btn btn-outline-ink justify-start"
              onClick={() => {
                setTeamsDraft(match.teams);
                setOptionsOpen(false);
              }}
            >
              🏷 Rename teams
            </button>
          )}
          <Link href={`/m/${code}/wheel`} className="btn btn-outline-ink justify-start">
            🎡 Dealer wheel {deals.length > 0 && '(result)'}
          </Link>
        </div>
      </Sheet>

      <Sheet
        open={rulesOpen}
        onClose={() => setRulesOpen(false)}
        title="House rules"
        footer={
          canEdit && (deals.length === 0 || rulesUnlocked) ? (
            <button type="button" className="btn btn-ink w-full" onClick={saveRules}>
              Save rules
            </button>
          ) : undefined
        }
      >
        {rulesDraft && (
          <HouseRulesPanel
            rules={rulesDraft}
            onChange={setRulesDraft}
            locked={!canEdit || (deals.length > 0 && !rulesUnlocked)}
            onUnlock={
              canEdit
                ? async () => {
                    const ok = await confirmDialog({
                      icon: '🔓',
                      title: 'Change the rules mid-match?',
                      body: (
                        <p>
                          All <span className="font-semibold text-ink">{deals.length} recorded deals</span> will be re-scored with the new rules,
                          which can change who is winning.
                        </p>
                      ),
                      confirmLabel: 'Unlock rules',
                      cancelLabel: 'Leave as is',
                    });
                    if (ok) setRulesUnlocked(true);
                  }
                : undefined
            }
          />
        )}
      </Sheet>

      <Sheet
        open={teamsDraft !== null}
        onClose={() => setTeamsDraft(null)}
        title="Rename teams"
        footer={
          <button
            type="button"
            className="btn btn-ink w-full"
            onClick={() => {
              if (!teamsDraft) return;
              const fixed: Teams = {
                A: { ...teamsDraft.A, name: cleanName(teamsDraft.A.name) || 'Team A' },
                B: { ...teamsDraft.B, name: cleanName(teamsDraft.B.name) || 'Team B' },
              };
              patchMatch(code, { teams: fixed });
              setTeamsDraft(null);
            }}
          >
            Save
          </button>
        }
      >
        {teamsDraft && (
          <div className="grid gap-4">
            {(['A', 'B'] as const).map((id) => (
              <div key={id}>
                <label className="label" htmlFor={`rename-${id}`}>
                  Team {id}
                </label>
                <input
                  id={`rename-${id}`}
                  dir="auto"
                  className="field name"
                  maxLength={24}
                  value={teamsDraft[id].name}
                  onChange={(e) => setTeamsDraft({ ...teamsDraft, [id]: { ...teamsDraft[id], name: e.target.value } })}
                />
              </div>
            ))}
          </div>
        )}
      </Sheet>
    </main>
  );
}
